import {makeAutoObservable} from 'mobx';

import type {Nullable} from '../../../utils/utility-types';
import type {RateConferenceConfig} from '../types';

import {conferenceAddFeedback} from '../../../services/entity-action/conference/conference.add-feedback';
import {not} from '../../../utils/conditionalUtils';
import {logger} from '../../../utils/logger';

type Config = Omit<RateConferenceConfig, 'id'>;

export class ConferenceRatingStore {
  audioQuality: Nullable<number> = null;
  coachingQuality: Nullable<number> = null;
  comment: Nullable<string> = null;
  videoQuality: Nullable<number> = null;
  private config: Config = {
    isB2b: false,
    role: 'coach',
    type: 'live',
  };
  private REQUIRED_COMMENT_RATING = 5;

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  get isSomeFieldFilled() {
    return (
      [this.audioQuality, this.videoQuality, this.coachingQuality].some(
        quality => quality !== null,
      ) || Boolean(this.comment?.length)
    );
  }

  get isSubmitDisabled() {
    return not(this.isSomeFieldFilled) || this.shouldShowCommentError;
  }

  get shouldShowCommentError() {
    const shouldSkipCheck =
      this.config.role === 'client' ||
      not(this.config.type === 'live') ||
      not(this.config.isB2b);

    if (shouldSkipCheck) {
      return false;
    }

    const marks: number[] = [];

    if (this.audioQuality !== null) {
      marks.push(this.audioQuality);
    }

    if (this.videoQuality !== null) {
      marks.push(this.videoQuality);
    }

    if (marks.length === 0) {
      return false;
    }

    const isHasComment = Boolean(this.comment?.length);

    return (
      not(isHasComment) &&
      marks.some(mark => mark <= this.REQUIRED_COMMENT_RATING)
    );
  }

  async sendFeedback(conferenceId: string) {
    try {
      await conferenceAddFeedback({
        comment: this.comment ?? undefined,
        id: conferenceId,
        ratings: {
          audio_quality: this.audioQuality ?? undefined,
          coaching_quality: this.coachingQuality ?? undefined,
          video_quality: this.videoQuality ?? undefined,
        },
        role: this.config.role,
        type: this.config.type,
      });
    } catch (e) {
      logger.error(e);
    }
  }

  setAudioQuality(value: number) {
    this.audioQuality = value;

    this.logRatingChange('audio', value);
  }

  setCoachingQuality(value: number) {
    this.coachingQuality = value;

    this.logRatingChange('coaching', value);
  }

  setComment(value: Nullable<string>) {
    this.comment = value;
  }

  setConfig(config: Config) {
    this.config = config;
  }

  setVideoQuality(value: number) {
    this.videoQuality = value;

    this.logRatingChange('video', value);
  }

  private logRatingChange(
    param: 'audio' | 'coaching' | 'video',
    value: number,
  ) {
    logger.event('session_rate_tap', {
      param,
      role: this.config.role,
      session_type: this.config.type,
      value,
    });
  }
}
